import { Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';

import Loading from '@/components/common/Loading';
import Tab, { TabItem } from '@/components/common/Tab';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { useTranslate, UseTranslateType } from '@/hooks/useTranslate';

import { DistributionRecord, getDistributionList, RewardType } from '../../service';

type TabActiveKey = 'point' | 'money';

const BenefitTip = ({ t }: { t: UseTranslateType }) => (
  <Tooltip
    classNames={{ root: 'small-tooltip' }}
    title={t('Distribution-Tips')}
    placement="bottom"
    color="var(--app-bg-popover)"
    styles={{
      body: {
        color: 'var(--app-text-primary)',
        border: '1px solid var(--app-border)',
      },
    }}
  >
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="17"
        viewBox="0 0 16 17"
        fill="none"
      >
        <path
          d="M8 11.1V7.85M8 5.51V5.5M14.25 8.5C14.25 11.9518 11.4518 14.75 8 14.75C4.54822 14.75 1.75 11.9518 1.75 8.5C1.75 5.04822 4.54822 2.25 8 2.25C11.4518 2.25 14.25 5.04822 14.25 8.5Z"
          stroke="#A4B8FD"
          strokeOpacity="0.51"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    </div>
  </Tooltip>
);

const formatTimestamp = (t: number) => {
  const date = new Date(t);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const POINT_VALIDITY_DAYS = 30;

const isPointExpired = (createTime: number) => {
  const expireTs = createTime + POINT_VALIDITY_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() > expireTs;
};

const formatCurrency = (currency?: string) => {
  return currency === 'USD' ? '$' : '¥';
};

const TabContent = ({
  isKOL,
  onChange,
}: {
  isKOL: boolean;
  onChange: (key: TabActiveKey) => void;
}) => {
  const { t } = useTranslate();
  const [activeKey, setActiveKey] = useState<TabActiveKey>('point');

  const handleChange = (key: TabActiveKey) => {
    setActiveKey(key);
    onChange(key);
  };

  if (!isKOL) {
    return <div className="pt-[0px]"></div>;
  }

  const tabItems: TabItem<TabActiveKey>[] = [
    {
      key: 'point',
      label: (activeKey) => (
        <span
          className={activeKey === 'point' ? 'text-app-primary' : 'text-app-tertiary'}
        >
          {t('Distribution-Tab-Points')}
        </span>
      ),
    },
    {
      key: 'money',
      label: (activeKey) => (
        <span
          className={activeKey === 'money' ? 'text-app-primary' : 'text-app-tertiary'}
        >
          {t('Distribution-Tab-Money')}
        </span>
      ),
    },
  ];

  return (
    <Tab
      className="gap-[20px]"
      itemClassName="text-[14px] font-[500] leading-[20px]"
      sliderClassName="!h-[2px] bg-[#5661F1] bottom-[-4px]"
      type="slider"
      sliderType="underline"
      items={tabItems}
      activeKey={activeKey}
      onChange={handleChange}
    />
  );
};

const extractList = (result: any): DistributionRecord[] => {
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.items)) return result.items;
  if (Array.isArray(result)) return result;
  return [];
};

const BenefitComponent = ({ isKOL }: { isKOL: boolean }) => {
  const { t } = useTranslate();

  const [activeKey, setActiveKey] = useState<TabActiveKey>('point');
  const [loading, setLoading] = useState(true);
  const [pointRecords, setPointRecords] = useState<DistributionRecord[]>([]);
  const [moneyRecords, setMoneyRecords] = useState<DistributionRecord[]>([]);
  const [pointPage, setPointPage] = useState(1);
  const [moneyPage, setMoneyPage] = useState(1);
  const [pointHasMore, setPointHasMore] = useState(true);
  const [moneyHasMore, setMoneyHasMore] = useState(true);
  const [statsData, setStatsData] = useState({
    friendsJoined: 0,
    pointsEarned: 0,
    moneyEarned: 0,
  });
  const PAGE_SIZE = 20;
  const loadingMore = useRef(false);

  // 初始化：一次性请求所有数据
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const pointResult = await getDistributionList<DistributionRecord>({
          recordState: 1,
          rewardType: RewardType.POINT,
          pageNum: 1,
          pageSize: PAGE_SIZE,
        });

        const pointList = extractList(pointResult);
        setPointRecords(pointList);
        setPointPage(1);
        setPointHasMore(pointList.length >= PAGE_SIZE);

        const stats = {
          friendsJoined: pointResult.totalCount || 0,
          pointsEarned: (pointResult.totalAmount || 0) / 60,
          moneyEarned: 0,
        };

        if (isKOL) {
          const moneyResult = await getDistributionList<DistributionRecord>({
            recordState: 1,
            rewardType: RewardType.MONEY,
            pageNum: 1,
            pageSize: PAGE_SIZE,
          });

          const moneyList = extractList(moneyResult);
          setMoneyRecords(moneyList);
          setMoneyPage(1);
          setMoneyHasMore(moneyList.length >= PAGE_SIZE);

          stats.friendsJoined = Math.max(
            pointResult.totalCount || 0,
            moneyResult.totalCount || 0,
          );
          stats.moneyEarned = (moneyResult.totalAmount || 0) / 100;
        }

        setStatsData(stats);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const currentRecords = activeKey === 'point' ? pointRecords : moneyRecords;
  const hasMore = activeKey === 'point' ? pointHasMore : moneyHasMore;

  const loadMore = async () => {
    if (loadingMore.current || !hasMore) return;
    loadingMore.current = true;

    const isPoint = activeKey === 'point';
    const nextPage = (isPoint ? pointPage : moneyPage) + 1;

    try {
      const result = await getDistributionList<DistributionRecord>({
        recordState: 1,
        rewardType: isPoint ? RewardType.POINT : RewardType.MONEY,
        pageNum: nextPage,
        pageSize: PAGE_SIZE,
      });

      const list = extractList(result);

      if (isPoint) {
        setPointRecords((prev) => [...prev, ...list]);
        setPointPage(nextPage);
        setPointHasMore(list.length >= PAGE_SIZE);
      } else {
        setMoneyRecords((prev) => [...prev, ...list]);
        setMoneyPage(nextPage);
        setMoneyHasMore(list.length >= PAGE_SIZE);
      }
    } catch {
      // ignore
    } finally {
      loadingMore.current = false;
    }
  };

  const { containerRef } = useInfiniteScroll({
    executeFn: loadMore,
    threshold: 100,
  });

  const onChangeTab = (key: TabActiveKey) => {
    setActiveKey(key);
  };

  return (
    <div className="px-[30px] pt-[10px] pb-[24px] gap-[20px] flex flex-col">
      {/* 统计卡片 */}
      <div
        className={`relative rounded-[12px] h-[92px] px-[26px] py-[15px] flex items-center justify-center ${isKOL ? 'gap-[14px]' : 'gap-[60px]'}`}
        style={
          {
            '--stat-gradient': 'linear-gradient(to right, #bb28ff, #327aff)',
            border: '2px solid transparent',
            backgroundImage:
              'linear-gradient(var(--app-bg-panel), var(--app-bg-panel)), linear-gradient(to right, #bb28ff, #327aff)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
          } as React.CSSProperties
        }
      >
        <div className="flex flex-col shrink-0">
          <p className="text-[14px] leading-[24px] text-app-secondary whitespace-nowrap">
            {t('Distribution-Friends-Joined')}
          </p>
          <p
            className="text-[28px] font-[700] leading-[40px] bg-clip-text text-transparent w-fit"
            style={{ backgroundImage: 'var(--stat-gradient)' }}
          >
            {statsData.friendsJoined}
          </p>
        </div>

        <div className="w-[0.5px] h-[70px] bg-app-active rounded-[12px]" />

        <div className="flex flex-col shrink-0">
          <div className="flex items-center gap-[4px]">
            <p className="text-[14px] leading-[24px] text-app-secondary whitespace-nowrap">
              {t('Distribution-Points-Earned')}
            </p>
          </div>
          <p
            className="text-[28px] font-[700] leading-[40px] bg-clip-text text-transparent w-fit"
            style={{ backgroundImage: 'var(--stat-gradient)' }}
          >
            {statsData.pointsEarned}
          </p>
        </div>

        {isKOL && (
          <>
            <div className="w-[0.5px] h-[70px] bg-app-active rounded-[12px]" />
            <div className="flex flex-col shrink-0">
              <p className="text-[14px] leading-[24px] text-app-secondary whitespace-nowrap">
                {t('Distribution-Money-Earned')}
              </p>
              <div className="flex items-center gap-[4px] text-transparent">
                <p
                  className="text-[28px] font-[700] leading-[40px] bg-clip-text"
                  style={{ backgroundImage: 'var(--stat-gradient)' }}
                >
                  {statsData.moneyEarned}
                </p>
                <p
                  className="text-[18px] font-[500] leading-[24px] bg-clip-text"
                  style={{ backgroundImage: 'var(--stat-gradient)' }}
                >
                  $
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      <TabContent isKOL={isKOL} onChange={onChangeTab} />

      <div className="bg-app-card rounded-[16px] overflow-hidden border border-app">
        <div className="h-[223px] scroll-overlay p-[16px] relative" ref={containerRef}>
          {loading && <Loading />}
          {currentRecords?.length
            ? currentRecords.map((item, index) => (
                <div
                  className={`flex justify-between items-center border-b border-[rgba(79,81,89,0.2)] ${index === currentRecords.length - 1 ? 'border-none' : ''}`}
                  key={index}
                >
                  <div
                    className={`flex flex-col items-start py-[8px] ${!index ? 'pt-[0px]' : ''} ${index === currentRecords.length - 1 ? 'pb-[0px] border-none' : ''}`}
                  >
                    <span className="text-[16px] font-[600] leading-[22px] text-app-primary pb-[2px] w-[200px] text-ellipsis overflow-hidden whitespace-nowrap ">
                      {item.invitedUserName || '-'}
                    </span>
                    {item.rewardType === RewardType.MONEY ? (
                      <span className="text-[12px] font-[600] leading-[16px] text-app-secondary">
                        {formatCurrency(item.orderCurrency)}
                        {((item.orderAmount || 0) / 100).toFixed(2) || '-'}
                      </span>
                    ) : (
                      <span className="text-[12px] font-[600] leading-[16px] text-app-secondary">
                        {t('Incentive-Benefit-Point-Source')}
                      </span>
                    )}
                    <span className="text-[12px] font-[500] leading-[16px] text-app-tertiary">
                      {formatTimestamp(item.createTime) || '-'}
                    </span>
                  </div>
                  {item.rewardType === RewardType.MONEY ? (
                    <div className="text-[#4931fa] text-[14px] font-[500] leading-[20px]">
                      {item.proceeds
                        ? `+${formatCurrency(item.orderCurrency!)}${((item.proceeds || 0) / 100).toFixed(2)}`
                        : '-'}
                    </div>
                  ) : (
                    <div className="flex flex-col items-end">
                      <div className="text-[#4931fa] text-[14px] font-[500] leading-[20px]">
                        +{item.proceeds ? item.proceeds / 60 : '-'}
                      </div>
                      <span className="text-[12px] font-[500] leading-[16px] text-[#FF4D4F]">
                        {isPointExpired(item.createTime)
                          ? t('Expired')
                          : `${t('Incentive-Point-Expire')} ${formatTimestamp(item.createTime + POINT_VALIDITY_DAYS * 24 * 60 * 60 * 1000)}`}
                      </span>
                    </div>
                  )}
                </div>
              ))
            : null}

          {!loading && !currentRecords.length ? (
            <div className="w-full h-full flex justify-center items-center flex-col">
              <span className="text-[14px] font-[500] leading-[20px] text-app-secondary">
                {t('Distribution-No')}
              </span>
              <span className="text-[12px] leading-[16px] text-app-tertiary">
                {activeKey === 'money'
                  ? t('Distribution-No-Money')
                  : t('Distribution-No1')}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default BenefitComponent;
